import React, { useEffect, useMemo, useState } from "react";
import {
  ArrayRemove,
  ArrayUnion,
  COLLECTION_ID,
  DELETE_FIRESTORE_FIELD,
  PUBLIC_ID,
  ROLE,
  Roles,
  Space,
} from "@highnote/server/src/core/entities";
import { ReactComponent as PinSVG } from "App/common/icons-v2/pushpin-line.svg";
import { ReactComponent as UnpinSVG } from "App/common/icons-v2/no-pushpin.svg";
import {
  getEntitySubscribers,
  PERMISSION,
} from "@highnote/server/src/core/shared-util";
import { highnote } from "@highnote/server/src/sdk";
import { Action, Actions } from "App/common/Actions";
import { ErrorBoundary } from "App/common/ErrorBoundary";
import { ReactComponent as ArchiveSVG } from "App/common/icons-v2/archive.svg";
import { ReactComponent as DownloadSVG } from "App/common/icons-v2/download.svg";
import { ReactComponent as InfoSVG } from "App/common/icons-v2/detail.svg";
import { ReactComponent as RenameSVG } from "App/common/icons-v2/edit.svg";
import { ReactComponent as NewSpaceSVG } from "App/common/icons-v2/folder-add-line.svg";
import { ReactComponent as MoveSVG } from "App/common/icons-v2/move.svg";
import { ReactComponent as BellMutedSVG } from "App/common/icons-v2/no-notification.svg";
import { ReactComponent as BellSVG } from "App/common/icons-v2/notification.svg";
import { ReactComponent as SettingsSVG } from "App/common/icons-v2/settings.svg";
import { ReactComponent as ShareSVG } from "App/common/icons-v2/share.svg";
import { ReactComponent as DeleteSVG } from "App/common/icons-v2/trash.svg";
import { ReactComponent as ExitSVG } from "App/common/icons/exit.svg";
import { ReactComponent as RemoveSVG } from "App/common/icons/remove.svg";
import { useConfirmation } from "App/common/useConfirmation";
import { useFileDownload } from "App/common/useFileDownload";
import { useUserDownloadRequests } from "App/components/useEntities/useDownloadRequests";
import { ARCHIVABLE_ENTITY_TYPES } from "@highnote/server/src/core/entities";
import { useAuth } from "App/components/Auth";
import { useCreateSpaceDialog } from "App/components/Dialog/CreateSpace/useCreateSpaceDialog";
import { useShareDialog } from "App/components/ShareDialog";
import { SPACE_EDITOR_TAB } from "App/components/SpaceEditor";
import { useSpaceEditor } from "App/components/useSpaceEditor";
import { getMoveToSpaceWarning } from "App/components/util";
import { countAllSpaceChildren } from "App/services/firebase/entities/helpers";
import { useGlobalSpaces } from "App/store/spaces/useGlobalSpaces";
import { useHistory } from "react-router";
import { useHighnote } from "./useHighnote";
import { useSpaceContext } from "./useSpace";
import { useToast } from "./useToast";
import { ButtonV2Props } from "App/core/ButtonV2";
import { useMoveSelectionsToTarget } from "App/components/useEntities/useMoveSelectionsToTarget";
import type { MenuProps } from "@mui/material/Menu";
import { useMembershipFlags } from "App/components/MembershipFlagsContext";

enum DEFAULT_SPACE_ACTION {
  SHARE = "share",
  DOWNLOAD = "download",
  RENAME = "rename",
  NEW_SPACE = "new-space",
  DETAILS = "details",
  SETTINGS = "settings",
  TOGGLE_ARCHIVE = "toggle-archive",
  MOVE_TO_SPACE = "move-to-space",
  DELETE = "delete",
  REMOVE = "remove",
  REMOVE_MYSELF = "remove-myself",
  TOGGLE_MUTE = "toggle-mute",
  TOGGLE_PIN_SPACE = "toggle-pin-space",
  // Not an action, just a way to add a separator to the menu
  SEPARATOR = "separator",
}

const SpaceActions = ({
  space,
  actions,
  buttonProps,
  hideTriggerButton,
  menuProps,
}: {
  space: Space;
  actions: (DEFAULT_SPACE_ACTION | Action)[];
  buttonProps?: ButtonV2Props;
  hideTriggerButton?: boolean;
  menuProps?: Partial<MenuProps>;
}) => {
  const history = useHistory();
  const { isAllowed, user } = useAuth();
  const { confirm, renderConfirmation } = useConfirmation();
  const { openShareDialog, renderShareDialog } = useShareDialog();
  const { openCreateSpaceDialog, renderCreateSpaceDialog } =
    useCreateSpaceDialog();
  const { toasted } = useToast();
  const { spaceId: activeSpaceId } = useSpaceContext();
  const { openMoveSelectionsToTarget } = useMoveSelectionsToTarget();
  const { moveToSpace } = useHighnote();
  const { downloadSpace, isDownloading, renderDownloadOption } =
    useFileDownload();
  const { getDownloadRequest } = useUserDownloadRequests();
  const isDownloadInProgress =
    isDownloading ||
    Boolean(getDownloadRequest(space.id, ARCHIVABLE_ENTITY_TYPES.SPACE));
  const subscribers = getEntitySubscribers(space);
  const isMember = subscribers.includes(user?.id);
  const mutedSpaces = user?.mutedSpaces || [];
  const muted = mutedSpaces.includes(space.id);
  const pinnedSpaces = user?.pinnedSpaces || [];
  const isPinnedSpace = pinnedSpaces.includes(space.id);
  const { openSpaceEditor } = useSpaceEditor();
  const { membersV2Enabled } = useMembershipFlags();

  const deleteSpace = async () => {
    try {
      const totalItemsCount = await countAllSpaceChildren(space);
      /*
       * The two branches show different numbers on purpose.
       *
       * The old expression counts the keys of `rolesV2`, minus the public
       * key. But share links (`share_<uuid>`) and pending email invites
       * (`invite:<email>`) are also `rolesV2` keys, so the old expression
       * counts them as people. The new `memberCount` field counts only
       * real members. So the number in the dialog goes down by the number
       * of share links and pending invites. Users can see that change. For
       * that reason the new expression sits behind the membership read
       * flag, together with the other new-model reads. It does not ship
       * alone as a bug fix.
       */
      const numPeople = membersV2Enabled
        ? space?.memberCount ?? 0
        : Object.keys(space.rolesV2 || {}).filter((r) => r !== PUBLIC_ID)
            .length;
      const messages = [
        ...(numPeople > 0
          ? [
              `This space is shared with ${numPeople} ${numPeople === 1 ? "person" : "people"}.`,
            ]
          : []),
        ...(totalItemsCount > 0
          ? [
              `All ${totalItemsCount} item(s) inside ${space.name} will be permanently deleted or removed.`,
            ]
          : []),
      ];

      await confirm({
        title: `Delete ${space.name}?`,
        body: (
          <p>
            {messages.join(" And ")}
            {messages.length > 0 ? (
              <>
                <br />
                <br />
              </>
            ) : null}
            <strong>Are you sure you want to delete {space.name}?</strong> This
            cannot be undone.
          </p>
        ),
      });

      await toasted({
        promise: highnote.deleteSpace({ id: space.id }),
        createMessage: `Deleting Space ${space.name}...`,
        successMessage: `Space ${space.name} deleted.`,
        errorMessage: `Could not delete Space ${space.name}. Please try again.`,
      });

      if (activeSpaceId === space.id) {
        history.push("/space");
      }
    } catch (e) {
      console.error("Error deleting space", e);
    }
  };

  const archiveSpace = () => {
    const verbRoot = space.isArchived ? "Unarchiv" : "Archiv";
    toasted({
      promise: highnote.updateSpace({
        id: space.id,
        data: { isArchived: !space.isArchived },
      }),
      createMessage: `${verbRoot}ing ${space.name}...`,
      errorMessage: `Could not ${verbRoot.toLowerCase()}e ${
        space.name
      }. Please try again.`,
      successMessage: `${verbRoot}ed ${space.name}.`,
    });
  };

  const removeFromSpace = async () => {
    const warning = await getMoveToSpaceWarning({
      entity: space,
      entityType: COLLECTION_ID.SPACE,
      spaceDestination: undefined,
      userId: user.id,
    });

    if (warning) {
      await confirm({
        title: warning.title,
        body: warning.description,
      });
    }

    moveToSpace({
      entityId: space.id,
      entityType: COLLECTION_ID.SPACE,
      newSpaceId: undefined,
    });
  };

  const updateEntityRoles = async (newRoles: Roles) => {
    const newRolesAsUpdateData: Roles = {};

    Object.entries(newRoles).forEach(([userId, userRoles]) => {
      newRolesAsUpdateData[`rolesV2,${userId}`] =
        userRoles || (DELETE_FIRESTORE_FIELD as unknown as ROLE[]);
    });

    await toasted({
      promise: highnote.updateSpace({
        id: space.id,
        data: newRolesAsUpdateData,
      }),
      createMessage: `Removing access from ${space.name}...`,
      successMessage: `Access removed from ${space.name}.`,
      errorMessage: `Could not remove access from ${space.name}. Please try again.`,
    });
  };

  const removeMyselfFromSpace = async () => {
    await confirm({
      title: `Removing yourself from ${space.name}`,
      body: (
        <p>
          <strong>
            {" "}
            Are you sure you want to remove yourself from {space.name}?
          </strong>{" "}
          You will no longer have access to this space.{" "}
        </p>
      ),
    });

    const newRoles: { [x: string]: ROLE[] } = {
      [user.id]: null,
    };

    try {
      await updateEntityRoles(newRoles);
    } catch (e) {
      console.error("Error removing access from space", e);
    }
  };

  const openSpaceShare = () => {
    openShareDialog(space);
  };

  const createNewSpace = () => {
    openCreateSpaceDialog({
      parentSpace: space,
    });
  };

  const pinSpace = async () => {
    await toasted({
      promise: highnote.updateUser({
        id: user.id,
        data: {
          pinnedSpaces: isPinnedSpace
            ? (new ArrayRemove([space.id]) as unknown as string[])
            : (new ArrayUnion([space.id]) as unknown as string[]),
        },
      }),
      errorMessage: `Could not pin ${space.name}. Please try again.`,
    });
  };

  if (!space) return null;

  return (
    <ErrorBoundary name="SpaceActions">
      <Actions
        buttonProps={buttonProps}
        menuProps={menuProps}
        hideTriggerButton={hideTriggerButton}
        actions={actions.map((action) => {
          if (action === DEFAULT_SPACE_ACTION.SEPARATOR) {
            return { name: "SEPARATOR" };
          }

          if (action === DEFAULT_SPACE_ACTION.NEW_SPACE) {
            const canAddSpaceToSpace = isAllowed(
              PERMISSION.TO_ADD_SPACE_TO_SPACE,
              { space },
            );
            return {
              name: "New Space",
              icon: <NewSpaceSVG />,
              onClick: createNewSpace,
              disabled: !canAddSpaceToSpace,
            };
          }

          if (action === DEFAULT_SPACE_ACTION.TOGGLE_PIN_SPACE) {
            if (!space.id) return null;

            return {
              name: isPinnedSpace ? "Unpin" : "Pin",
              icon: isPinnedSpace ? <UnpinSVG /> : <PinSVG />,
              onClick: pinSpace,
            };
          }

          if (action === DEFAULT_SPACE_ACTION.SHARE) {
            return {
              name: "Share",
              icon: <ShareSVG />,
              onClick: openSpaceShare,
            };
          }

          if (action === DEFAULT_SPACE_ACTION.DOWNLOAD) {
            return {
              name: isDownloadInProgress
                ? "Downloading Space..."
                : "Download Space",
              icon: <DownloadSVG />,
              onClick: () => downloadSpace(space.id),
              disabled: isDownloadInProgress,
            };
          }

          if (action === DEFAULT_SPACE_ACTION.TOGGLE_ARCHIVE) {
            if (!isAllowed(PERMISSION.TO_ARCHIVE_SPACE, { space })) return null;
            return {
              name: space.isArchived ? "Unarchive" : "Archive",
              icon: <ArchiveSVG />,
              onClick: archiveSpace,
            };
          }

          if (action === DEFAULT_SPACE_ACTION.RENAME) {
            if (!isAllowed(PERMISSION.TO_MANAGE_SPACE, { space })) return null;
            return {
              name: "Rename",
              icon: <RenameSVG />,
              onClick: () => openSpaceEditor(space),
            };
          }

          if (action === DEFAULT_SPACE_ACTION.MOVE_TO_SPACE) {
            return {
              name: "Move to...",
              icon: <MoveSVG />,
              onClick: () =>
                openMoveSelectionsToTarget({
                  selectedEntities: {
                    [space.id]: {
                      entityId: space.id,
                      entityType: COLLECTION_ID.SPACE,
                      parentEntity: {
                        entityId: space.spaceId,
                        entityType: COLLECTION_ID.SPACE,
                      },
                    },
                  },
                }),
            };
          }

          if (action === DEFAULT_SPACE_ACTION.TOGGLE_MUTE) {
            if (!isMember) return null;
            return {
              name: muted ? "Unmute" : "Mute",
              icon: muted ? <BellSVG /> : <BellMutedSVG />,
              onClick: () => {
                highnote.updateUser({
                  id: user.id,
                  data: {
                    mutedSpaces: muted
                      ? (new ArrayRemove([space.id]) as unknown as string[])
                      : (new ArrayUnion([space.id]) as unknown as string[]),
                  },
                });
              },
            };
          }

          if (action === DEFAULT_SPACE_ACTION.REMOVE) {
            if (!space.spaceId) return null;
            return {
              name: "Remove from Space",
              icon: <RemoveSVG />,
              onClick: removeFromSpace,
            };
          }

          if (action === DEFAULT_SPACE_ACTION.DELETE) {
            if (!isAllowed(PERMISSION.TO_DELETE_SPACE, { space })) return null;
            return {
              name: "Delete Space",
              icon: <DeleteSVG />,
              onClick: deleteSpace,
              warn: true,
            };
          }

          if (action === DEFAULT_SPACE_ACTION.DETAILS) {
            return {
              name: "Info",
              onClick: () => {
                openSpaceEditor(space);
              },
              icon: <InfoSVG />,
            };
          }

          if (action === DEFAULT_SPACE_ACTION.SETTINGS) {
            return {
              name: "Settings",
              onClick: () => {
                openSpaceEditor(space, SPACE_EDITOR_TAB.SETTINGS);
              },
              icon: <SettingsSVG />,
            };
          }

          if (action === DEFAULT_SPACE_ACTION.REMOVE_MYSELF) {
            return {
              name: "Remove Myself",
              onClick: removeMyselfFromSpace,
              icon: <ExitSVG />,
              warn: true,
            };
          }

          return action;
        })}
      />
      {renderConfirmation}
      {renderShareDialog}
      {renderCreateSpaceDialog}
      {renderDownloadOption}
    </ErrorBoundary>
  );
};

export const DefaultSpaceActions = ({
  space,
  buttonProps,
  hideTriggerButton,
  menuProps,
}: {
  space: Space;
  buttonProps?: ButtonV2Props;
  hideTriggerButton?: boolean;
  menuProps?: Partial<MenuProps>;
}) => {
  const { isAllowed } = useAuth();
  const { getGlobalSpace } = useGlobalSpaces();
  const [parentSpace, setParentSpace] = useState<Space | undefined>();

  useEffect(() => {
    let isMounted = true;

    const fetchParentSpace = async () => {
      if (!space.spaceId) return;

      const globalSpace = getGlobalSpace(space.spaceId);
      if (globalSpace) {
        if (isMounted) {
          setParentSpace(globalSpace);
        }
      } else {
        try {
          const fetchedSpace = await highnote.getSpace({
            id: space.spaceId,
            fromCache: true,
          });
          if (isMounted) {
            setParentSpace(fetchedSpace);
          }
        } catch (error) {
          console.error("Error getting parent space", error);
        }
      }
    };

    fetchParentSpace();

    return () => {
      isMounted = false;
    };
  }, [space.spaceId, getGlobalSpace]);

  const actions = useMemo(() => {
    const canArchive = isAllowed(PERMISSION.TO_ARCHIVE_SPACE, { space });
    const canDelete = isAllowed(PERMISSION.TO_DELETE_SPACE, { space });
    const canDownload = isAllowed(PERMISSION.TO_DOWNLOAD_SPACE, { space });
    const canManage = isAllowed(PERMISSION.TO_MANAGE_SPACE, { space });
    const canManageParent =
      !space.spaceId ||
      isAllowed(PERMISSION.TO_MANAGE_SPACE, {
        space: parentSpace,
      });

    const canRemoveMyself = isAllowed(PERMISSION.TO_REMOVE_SELF_FROM_SPACE, {
      space,
    });

    if (canManage) {
      return [
        DEFAULT_SPACE_ACTION.SHARE,
        ...(canDownload ? [DEFAULT_SPACE_ACTION.DOWNLOAD] : []),
        ...(canManageParent
          ? [
              DEFAULT_SPACE_ACTION.SEPARATOR,
              DEFAULT_SPACE_ACTION.DETAILS,
              DEFAULT_SPACE_ACTION.RENAME,
              DEFAULT_SPACE_ACTION.SETTINGS,
              DEFAULT_SPACE_ACTION.SEPARATOR,
              DEFAULT_SPACE_ACTION.NEW_SPACE,
              DEFAULT_SPACE_ACTION.MOVE_TO_SPACE,
              // If needed, add DEFAULT_SPACE_ACTION.REMOVE here
              // https://github.com/highnotefm/highnote/pull/991/files
            ]
          : []),
        DEFAULT_SPACE_ACTION.SEPARATOR,
        DEFAULT_SPACE_ACTION.TOGGLE_MUTE,
        DEFAULT_SPACE_ACTION.TOGGLE_PIN_SPACE,
        ...(canArchive ? [DEFAULT_SPACE_ACTION.TOGGLE_ARCHIVE] : []),
        ...(canRemoveMyself
          ? [DEFAULT_SPACE_ACTION.SEPARATOR, DEFAULT_SPACE_ACTION.REMOVE_MYSELF]
          : []),
        ...(canDelete
          ? [DEFAULT_SPACE_ACTION.SEPARATOR, DEFAULT_SPACE_ACTION.DELETE]
          : []),
      ];
    }

    return [
      ...(canDownload
        ? [DEFAULT_SPACE_ACTION.DOWNLOAD, DEFAULT_SPACE_ACTION.SEPARATOR]
        : []),
      DEFAULT_SPACE_ACTION.TOGGLE_MUTE,
      DEFAULT_SPACE_ACTION.TOGGLE_PIN_SPACE,
      DEFAULT_SPACE_ACTION.SEPARATOR,
      DEFAULT_SPACE_ACTION.DETAILS,
      DEFAULT_SPACE_ACTION.SETTINGS,
      // If needed, add DEFAULT_SPACE_ACTION.REMOVE here if they canManageParent
      // https://github.com/highnotefm/highnote/pull/991/files
      ...(canRemoveMyself
        ? [DEFAULT_SPACE_ACTION.SEPARATOR, DEFAULT_SPACE_ACTION.REMOVE_MYSELF]
        : []),
    ];
  }, [isAllowed, space, parentSpace]);

  return (
    <SpaceActions
      space={space}
      actions={actions}
      buttonProps={buttonProps}
      hideTriggerButton={hideTriggerButton}
      menuProps={menuProps}
    />
  );
};
